import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import {
  Card,
  Empty,
  ErrorBox,
  Filter,
  Ltr,
  Row,
  Spinner,
  StatusChip,
} from '../components/ui.js';
import type { IssueSummary } from '../lib/types.js';

type View = 'board' | 'backlog' | 'sprints' | 'epics';

interface Board {
  id: number;
  name: string;
  type: string;
}

interface Sprint {
  id: number;
  name: string;
  state: string;
  goal?: string;
}

interface Epic {
  id: number;
  key: string;
  name: string;
  done: boolean;
}

interface BoardView {
  board: { id: number; name: string; type: string };
  sprint: { id: number; name: string; state: string } | null;
  columns: Array<{ name: string; issues: IssueSummary[] }>;
  unmapped: IssueSummary[];
  total: number;
  totalOnBoard: number;
  truncated: boolean;
  hasActiveSprint: boolean;
}

/**
 * A kanban board genuinely has no backlog and runs no sprints — Jira answers
 * 400 for both. Offering the tabs and then showing that as an error read as a
 * broken page, so the board's own type decides which views exist.
 */
function viewsFor(board: Board | undefined): View[] {
  if (!board) return ['board'];
  return board.type === 'scrum'
    ? ['board', 'backlog', 'sprints', 'epics']
    : ['board', 'epics'];
}

export function AgilePage(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [boardId, setBoardId] = useState<number | null>(null);
  const [view, setView] = useState<View>('board');

  const boards = useQuery({
    queryKey: ['boards'],
    queryFn: () => api<{ items: Board[] }>('/boards?startAt=0'),
  });

  const items = boards.data?.items ?? [];
  const board = items.find((entry) => entry.id === boardId);
  const views = viewsFor(board);

  // Land on something rather than an empty screen that needs a tap first.
  useEffect(() => {
    if (boardId === null && items.length > 0) setBoardId(items[0]!.id);
  }, [boardId, items]);

  useEffect(() => {
    if (!views.includes(view)) setView('board');
  }, [views, view]);

  const boardView = useQuery<BoardView | null>({
    queryKey: ['board-view', boardId],
    queryFn: () => (boardId === null ? null : api<BoardView>(`/boards/${boardId}/view`)),
    enabled: boardId !== null && view === 'board',
  });

  const content = useQuery<{ items: unknown[] } | null>({
    queryKey: ['agile', boardId, view],
    queryFn: async () => {
      if (boardId === null) return null;
      if (view === 'backlog') return api<{ items: unknown[] }>(`/boards/${boardId}/backlog`);
      if (view === 'sprints') return api<{ items: unknown[] }>(`/boards/${boardId}/sprints`);
      return api<{ items: unknown[] }>(`/boards/${boardId}/epics`);
    },
    enabled: boardId !== null && view !== 'board',
  });

  const viewLabel: Record<View, string> = {
    board: t('agile.board'),
    backlog: t('agile.backlog'),
    sprints: t('agile.sprints'),
    epics: t('agile.epics'),
  };

  if (boards.isLoading) return <Spinner />;

  return (
    <div className="page">
      <div className="header">
        <h1>{t('agile.title')}</h1>
        {board ? (
          <span className="muted small">
            {board.type === 'scrum' ? t('agile.scrum') : t('agile.kanban')}
          </span>
        ) : null}
      </div>

      {boards.error ? <ErrorBox error={boards.error} /> : null}

      {items.length === 0 && !boards.error ? (
        <Card>
          <Empty text={t('agile.no_boards')} />
        </Card>
      ) : (
        <div className="filter-row">
          {items.map((entry) => (
            <Filter
              key={entry.id}
              label={`${entry.type === 'scrum' ? '🏃' : '📋'} ${entry.name}`}
              active={boardId === entry.id}
              onClick={() => setBoardId(entry.id)}
            />
          ))}
        </div>
      )}

      {boardId !== null ? (
        <div className="filter-row">
          {views.map((item) => (
            <Filter
              key={item}
              label={viewLabel[item]}
              active={view === item}
              onClick={() => setView(item)}
            />
          ))}
        </div>
      ) : null}

      {view === 'board' ? (
        <>
          {boardView.isLoading ? <Spinner /> : null}
          {boardView.error ? <BoardError error={boardView.error} /> : null}
          {boardView.data && !boardView.error ? (
            <BoardColumns view={boardView.data} onOpen={(key) => navigate(`/issues/${key}`)} />
          ) : null}
        </>
      ) : null}

      {content.isLoading ? <Spinner /> : null}

      {/*
        One board the person cannot open must not blank the whole screen: the
        board list above stays, and only this panel explains itself.
      */}
      {content.error ? <BoardError error={content.error} /> : null}

      {view !== 'board' && content.data && !content.error ? (
        <Card>
          {content.data.items.length === 0 ? (
            <Empty text={t('agile.board_empty')} />
          ) : view === 'sprints' ? (
            (content.data.items as Sprint[]).map((sprint) => (
              <Row
                key={sprint.id}
                title={sprint.name}
                subtitle={sprint.goal ?? ''}
                right={<SprintState state={sprint.state} />}
              />
            ))
          ) : view === 'epics' ? (
            (content.data.items as Epic[]).map((epic) => (
              <Row
                key={epic.id}
                onClick={() => navigate(`/issues/${epic.key}`)}
                title={epic.name}
                subtitle={<Ltr>{epic.key}</Ltr>}
                right={
                  <span className={`chip ${epic.done ? 'chip-done' : 'chip-new'}`}>
                    {epic.done ? t('epic.done') : t('epic.open')}
                  </span>
                }
              />
            ))
          ) : (
            (content.data.items as IssueSummary[]).map((issue) => (
              <Row
                key={issue.key}
                onClick={() => navigate(`/issues/${issue.key}`)}
                title={
                  <>
                    <Ltr>{issue.key}</Ltr> · {issue.summary}
                  </>
                }
                subtitle={issue.assignee?.displayName ?? t('common.unassigned')}
                right={<StatusChip name={issue.statusName} category={issue.statusCategoryKey} />}
              />
            ))
          )}
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Jira's sprint states, said plainly and coloured by what they mean. The raw
 * API words (`active` / `future` / `closed`) told the reader nothing about
 * whether anything was actually running.
 */
function SprintState({ state }: { state: string }): JSX.Element {
  const { t } = useI18n();
  const known: Record<string, { key: string; tone: string }> = {
    active: { key: 'sprint.active', tone: 'chip-done' },
    future: { key: 'sprint.future', tone: 'chip-indeterminate' },
    closed: { key: 'sprint.closed', tone: 'chip-new' },
  };
  const entry = known[state.toLowerCase()];
  return <span className={`chip ${entry?.tone ?? ''}`}>{entry ? t(entry.key) : state}</span>;
}

/**
 * The board drawn as Jira defines it: one section per column, in the board's
 * own order, holding the issues whose status that column claims.
 */
function BoardColumns({
  view,
  onOpen,
}: {
  view: BoardView;
  onOpen: (issueKey: string) => void;
}): JSX.Element {
  const { t } = useI18n();

  if (view.board.type === 'scrum' && !view.hasActiveSprint) {
    return <NoActiveSprint boardId={view.board.id} />;
  }

  const sections = [
    ...view.columns,
    ...(view.unmapped.length > 0
      ? [{ name: t('agile.unmapped'), issues: view.unmapped }]
      : []),
  ];

  return (
    <>
      {view.sprint ? (
        <div className="note small">
          🏃 {t('agile.sprint_now')}: <strong>{view.sprint.name}</strong> ·{' '}
          {t('agile.issues_count', { count: view.total })}
        </div>
      ) : null}

      {view.truncated ? (
        <div className="note small">
          {t('agile.truncated', { shown: view.total, total: view.totalOnBoard })}
        </div>
      ) : null}

      {sections.map((column) => (
        <Card key={column.name} title={`${column.name} · ${column.issues.length}`}>
          {column.issues.length === 0 ? (
            <Empty text={t('agile.column_empty')} />
          ) : (
            column.issues.map((issue) => (
              <Row
                key={issue.key}
                onClick={() => onOpen(issue.key)}
                title={
                  <>
                    <Ltr>{issue.key}</Ltr> · {issue.summary}
                  </>
                }
                subtitle={issue.assignee?.displayName ?? t('common.unassigned')}
                right={
                  issue.priorityName ? <span className="chip">{issue.priorityName}</span> : null
                }
              />
            ))
          )}
        </Card>
      ))}
    </>
  );
}

/**
 * A scrum board with nothing running. Saying only "no active sprint" leaves the
 * reader wondering whether the board is broken, so its sprints are listed with
 * their state — the absence is then visibly a fact about the board, not a
 * failure to load.
 */
function NoActiveSprint({ boardId }: { boardId: number }): JSX.Element {
  const { t } = useI18n();
  const sprints = useQuery({
    queryKey: ['agile', boardId, 'sprints'],
    queryFn: () => api<{ items: Sprint[] }>(`/boards/${boardId}/sprints`),
  });

  const items = sprints.data?.items ?? [];

  return (
    <Card>
      <Empty text={t('agile.no_active_sprint')} />
      {sprints.isLoading ? <Spinner /> : null}
      {items.length > 0 ? (
        <>
          <p className="small muted" style={{ margin: '0 0 4px' }}>
            {t('agile.no_active_sprint_hint')}
          </p>
          {items.map((sprint) => (
            <Row key={sprint.id} title={sprint.name} right={<SprintState state={sprint.state} />} />
          ))}
        </>
      ) : null}
      {!sprints.isLoading && items.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>
          {t('agile.sprint_none')}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Jira refusing a view is information, not a failure. "This board has no
 * backlog" and "you may not see this" are both normal answers and read better
 * as a sentence than as a red error box.
 */
function BoardError({ error }: { error: unknown }): JSX.Element {
  const { t } = useI18n();
  const code = error instanceof ApiError ? error.code : null;
  const detail = error instanceof ApiError ? (error.detail ?? '') : '';

  if (code === 'JiraValidationError' && /backlog/i.test(detail)) {
    return (
      <Card>
        <Empty text={t('agile.kanban_no_backlog')} />
      </Card>
    );
  }
  if (code === 'JiraValidationError' && /sprint/i.test(detail)) {
    return (
      <Card>
        <Empty text={t('agile.kanban_no_sprints')} />
      </Card>
    );
  }
  if (code === 'JiraForbidden' || code === 'JiraNotFound') {
    return (
      <Card>
        <Empty text={t('error.JiraForbidden')} />
      </Card>
    );
  }
  return <ErrorBox error={error} />;
}

export { viewsFor };
